/**
 * AI Analyst copy: message chrome (steps, status line, memory card, follow-ups, attachments, answer actions, usage meter, debug panel, avatar).
 * Ported verbatim from VentaHub; wired under `aiAnalyst` in `en.ts`.
 */
export const aiAnalystMessage = {
  // StepList / StepsSummary
  steps: {
    analysedIn_one: 'Analysed in {{count}} step',
    analysedIn_other: 'Analysed in {{count}} steps',
    running: 'Running',
    done: 'Done',
    failed: 'Failed',
    retried: 'Retried',
  },
  // StatusLine
  status: {
    queued: 'The analyst is busy. Your question is queued and will start shortly…',
    elapsed: 'Elapsed time',
    hint: '· {{hint}}',
    earlierSteps_one: '+{{count}} earlier step',
    earlierSteps_other: '+{{count}} earlier steps',
  },
  // MemoryCard
  memory: {
    remember: 'Remember this?',
    rememberForCompany: 'Remember this for your company?',
    saved: 'Saved to memory',
    saveFailed: "Couldn't save it. Try again.",
    notNow: 'Not now',
    rememberButton: 'Remember',
    saving: 'Saving…',
  },
  // Followups
  followups: {
    label: 'Suggested follow-up questions',
    heading: 'Ask next',
  },
  // SuggestDeep
  suggest: {
    run: 'Run a deep analysis',
  },
  // AttachmentChips
  attachments: {
    label: 'Attachments',
    skus_one: '{{count}} SKU',
    skus_other: '{{count}} SKUs',
    moreCodes: '{{codes}}…',
    about: 'About: {{title}}',
    removeSkus: 'Remove SKU list',
    removePage: 'Remove page',
  },
  // AnswerActions
  actions: {
    toolbar: 'Answer actions',
    copy: 'Copy',
    copyAnswer: 'Copy answer',
    copyText: 'Copy text',
    copyMarkdown: 'Copy as Markdown',
    copyFormatted: 'Copy with formatting',
    copied: 'Copied',
    copiedMarkdown: 'Copied as Markdown',
    copiedFormatted: 'Copied with formatting',
    copyFailed: "Couldn't copy",
    helpful: 'Helpful',
    notHelpful: 'Not helpful',
    retry: 'Retry',
    askAgain: 'Ask again',
    saveReport: 'Save as report',
    savingReport: 'Saving…',
    savedReport: 'Saved to Reports',
    saveReportFailed: "Couldn't save",
    feedbackFailed: "Couldn't save your feedback.",
    dialogTitle: 'What went wrong?',
    comment: 'Anything else? (optional)',
    reviewNote: 'The team reviews every answer marked not helpful.',
    cancel: 'Cancel',
    send: 'Send feedback',
    reasons: {
      wrong_number: 'Wrong number',
      wrong_scope: 'Wrong period or scope',
      not_answered: "Didn't answer my question",
      too_slow: 'Too slow',
      other: 'Other',
    },
  },
  // UsageMeter
  usage: {
    label: "Today's AI Analyst allowance used",
    loading: "Loading today's allowance",
    usedUp: "You've used today's allowance",
    ofAllowance: "of today's allowance used",
    percentUsed: "{{percent}} of today's allowance used",
    resets: '· {{resetsIn}}',
    questions: 'Questions: {{used}} of {{limit}} ({{runsPercent}}). Usage: {{spendPercent}}.',
    remaining_one: 'About {{count}} more question today.',
    remaining_other: 'About {{count}} more questions today.',
    higherOfTwo: 'The higher of the two counts. Resets at midnight {{zone}}.',
  },
  // DebugPanel
  debug: {
    toggle: 'Debug details',
    title: 'Debug',
    live_one: 'live · {{count}} query so far',
    live_other: 'live · {{count}} queries so far',
    history: 'SQL, reasoning and usage',
    tabs: {
      queries: 'Queries ({{n}})',
      reasoning: 'Reasoning ({{n}})',
      calls: 'Calls & look-ups ({{n}})',
      usage: 'Usage',
    },
    nothing: 'Nothing recorded.',
    usagePending: 'Usage is reported when the answer finishes.',
    loading: 'Loading the trace…',
    loadFailed: 'The trace could not be loaded.',
    cooldown: 'Not called: the provider is on cool-down.',
    sqlWritten: 'SQL written',
    sqlRun: 'SQL run (scoped)',
    endpoint: 'Endpoint {{name}}',
    detail: 'Detail',
    copyLabel: 'Copy {{label}}',
    copied: 'Copied',
    copyFailed: "Couldn't copy",
    rows_one: '{{count}} row',
    rows_other: '{{count}} rows',
    ms: '{{ms}} ms',
    joiner: ' · ',
    fallbackFrom: '{{served}} (fallback from {{configured}})',
    usageRows: {
      analystModel: 'Analyst model',
      workerModel: 'Worker model',
      depth: 'Depth',
      inputTokens: 'Input tokens',
      outputTokens: 'Output tokens',
      cache: 'Cache read / write',
      cacheValue: '{{read}} / {{write}}',
      cost: 'Cost',
      queries: 'Queries',
      time: 'Time',
    },
  },
} as const
